import {
  Box,
  FormControl,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { SupportedLanguage } from "@pimo/pimo-l10n";

import { Client } from "../types/entities";
import { Profile } from "../types/profile";
import { AccountSettingsDropdown } from "./account-settings-dropdown";

export interface PageHeaderProps {
  client?: Client;
  clients: Client[];
  userProfile?: Profile;
  onSwitchClient: (clientId: string) => void;
  onLogout: () => void;
  onOpenAdmin: () => void;
  onChangeLanguage: (language?: SupportedLanguage) => void;
  language: SupportedLanguage;
}

// Company names need not be unique; add the unique account slug when two accounts share one.
const clientLabel = (client: Client, clients: Client[]) => {
  const label = client.companyName || client.name;
  const isShared = clients.some(
    (other) =>
      other.id !== client.id && (other.companyName || other.name) === label
  );
  return isShared ? `${label} (${client.name})` : label;
};

export const PageHeader = ({
  client,
  clients,
  userProfile,
  onSwitchClient,
  onLogout,
  onOpenAdmin,
  onChangeLanguage,
  language,
}: PageHeaderProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: 3,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 500, color: "#333", fontSize: "2rem" }}
      >
        Home Portal
      </Typography>

      {clients.length > 1 ? (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={client?.id ?? ""}
            onChange={(e: SelectChangeEvent) => onSwitchClient(e.target.value)}
            sx={{
              borderRadius: 2,
              bgcolor: "#f8f9fa",
              fontWeight: 500,
              fontSize: "0.95rem",
              color: "#333",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3ec6ff",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3ec6ff",
              },
            }}
          >
            {clients.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {clientLabel(c, clients)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : client ? (
        <Typography
          variant="h4"
          sx={{ fontWeight: 400, color: "#666", fontSize: "2rem" }}
        >
          - {clientLabel(client, clients)}
        </Typography>
      ) : null}
    </Box>

    <AccountSettingsDropdown
      userProfile={userProfile}
      selectedLanguage={language}
      fireEvent={(event, payload) => {
        if (event === "logout") onLogout();
        if (event === "open-admin") onOpenAdmin();
        if (event === "change-language") onChangeLanguage(payload?.language);
      }}
    />
  </Box>
);
